// ============================================================================
// Safe Care - Gestion des cotisations (échéances + prélèvements)
// Module : features/assistance_sociale/pages
// Route  : /assurance/cotisations
// Charte : .tab / .formTitle / .bG / .clientP / .tabItem (identique à
//          /prets/simulation) — les actions sont sur la barre .bG.
// Export : même système que /assurance/visualisation — <ExportFileModal />
//          (carte animée -> Visualiser / Plus tard) via services/exportFile.
// ============================================================================

import { useState, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input, Tag, Select } from 'antd';
import type { TabsProps } from 'antd';
import { MoneyCollectOutlined } from '@ant-design/icons';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import dayjs from 'dayjs';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import ExportMenu, { EXPORT_FORMATS_DOCS } from '../../../Components/ExportMenu';
import ExportFileModal from '../components/ExportFileModal';
import { ExportFile, FormatExport, sectionsFile } from '../services/exportFile';
import {
  useGetAllSouscriptions,
  useGetEcheancesBySouscription,
  useGenererEcheances,
  usePreleverEcheance,
} from '../hooks';
import { SouscriptionSafeCare, EcheanceAssistance } from '../types';

const CotisationsPage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const { data: souscriptions, isLoading, refetch } = useGetAllSouscriptions('ACTIVE');
  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [modalPrelever, setModalPrelever] = useState(false);
  const [selectedEcheance, setSelectedEcheance] = useState<EcheanceAssistance | null>(null);
  const [codope, setCodope] = useState('');
  const [loading, setLoading] = useState(false);
  const [formatExport, setFormatExport] = useState<FormatExport>('pdf');
  const [exportFile, setExportFile] = useState<ExportFile | null>(null);

  const { data: echeancesRes, isLoading: loadingEcheances, refetch: refetchEcheances } = useGetEcheancesBySouscription(
    selectedSouscription?.IdSouscription
  );
  const genererEcheances = useGenererEcheances();
  const preleverEcheance = usePreleverEcheance();

  const echeances: EcheanceAssistance[] =
    echeancesRes?.StatutCode === 200 ? echeancesRes.Result?.Table1 ?? [] : [];

  const dataSouscriptions = souscriptions ?? [];
  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return dataSouscriptions;
    const term = searchTerm.toLowerCase();
    return dataSouscriptions.filter((d) =>
      [d.Numcli, d.NOMCLI, d.PRECLI, d.NomProduit, d.NumeroVersion, d.NumCptDebit, d.Statut, d.MotifStatut]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [dataSouscriptions, searchTerm]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = dataSouscriptions.find((s) => s.IdSouscription === newSelectedRowKeys[0]) || null;
    setSelectedSouscription(record);
  };
  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    type: 'radio' as const,
    onChange: onSelectChange,
  };

  function handleGenerer() {
    if (!selectedSouscription) {
      SweetAlert.fire({ title: 'Sélectionnez une souscription', icon: 'warning' });
      return;
    }
    SweetAlert.fire({
      title: 'Génération des échéances',
      text: `Générer les échéances pour ${selectedSouscription.NomProduit} ?`,
      showCancelButton: true,
      icon: 'question',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        genererEcheances.mutate(
          { idSouscription: selectedSouscription.IdSouscription!, utiSai: user.coduti ?? '' },
          {
            onSuccess: (res) => {
              setLoading(false);
              if (res?.StatutCode === 200) {
                SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
              } else {
                SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
              }
            },
          }
        );
      }
    });
  }

  function openPrelever(record: EcheanceAssistance) {
    setSelectedEcheance(record);
    // Code opération repris du produit Safe Care de la souscription (CodOpeCotisation)
    setCodope(selectedSouscription?.CodOpeCotisation ?? '');
    setModalPrelever(true);
  }

  function confirmPrelever() {
    if (!selectedEcheance) return;
    setLoading(true);
    preleverEcheance.mutate(
      { idEcheance: selectedEcheance.IdEcheance!, codope, utiSai: user.coduti ?? '' },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalPrelever(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
      }
    );
  }

  // Export : même flux que /assurance/visualisation — le fichier est généré en
  // mémoire (Blob) puis la carte animée <ExportFileModal/> propose Visualiser /
  // Plus tard ; le téléchargement n'a lieu que sur l'une des deux actions.
  const exporter = async (fmt?: 'pdf' | 'xlsx' | 'docx') => {
    const format = fmt ?? formatExport;
    if (!selectedSouscription || !echeances.length) {
      SweetAlert.fire({ title: 'Sélectionnez une souscription avec des échéances', icon: 'warning' });
      return;
    }
    setFormatExport(format);
    const s = selectedSouscription;
    const sections = [
      {
        titre: 'Informations',
        data: [
          { Champ: 'Client', Valeur: `${s.Numcli ?? ''} - ${s.NOMCLI ?? ''} ${s.PRECLI ?? ''}`.trim() },
          { Champ: 'Produit', Valeur: `${s.NomProduit ?? ''} - Version ${s.NumeroVersion ?? ''}` },
          { Champ: 'Durée du contrat', Valeur: s.DureeContratMois != null ? `${s.DureeContratMois} mois` : '-' },
          { Champ: 'Période de paiement', Valeur: s.LibelleFrequence ?? s.CodeFrequence ?? '-' },
          { Champ: 'Date effet', Valeur: s.DateEffet ? dayjs(s.DateEffet).format('DD/MM/YYYY') : '-' },
          { Champ: 'Date fin', Valeur: s.DateFin ? dayjs(s.DateFin).format('DD/MM/YYYY') : '-' },
          { Champ: 'Montant cotisation', Valeur: s.MontantCotisation ?? '-' },
        ],
      },
      {
        titre: 'Échéances',
        data: echeances.map((e) => ({
          'Date échéance': e.DateEcheance ? dayjs(e.DateEcheance).format('DD/MM/YYYY') : '',
          'Montant': e.MontantCotisation,
          'Montant payé': e.MontantPaye ?? 0,
          'Reste à payer': (e.MontantCotisation - (e.MontantPaye ?? 0)).toFixed(2),
          'Date paiement': e.DatePaiement ? dayjs(e.DatePaiement).format('DD/MM/YYYY') : '',
          'Statut': e.Statut,
        })),
      },
    ];
    setExportFile(await sectionsFile(
      format,
      `Tableau d'amortissement - Souscription #${s.IdSouscription}`,
      `amortissement_souscription_${s.IdSouscription}`,
      sections
    ));
  };

  const actualiser = () => {
    setLoading(true);
    Promise.all([refetch(), refetchEcheances()]).finally(() => {
      setLoading(false);
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    });
  };

  const annuler = () => {
    setSelectedRowKeys([]);
    setSelectedSouscription(null);
    setSearchTerm('');
  };

  const statutColor = (statut: string) => {
    switch (statut) {
      case 'PAYEE': return 'green';
      case 'IMPAYEE': return 'red';
      case 'A_PAYER': return 'orange';
      case 'ANNULEE': return 'default';
      default: return 'default';
    }
  };

  const columnsSous: ColumnsType<SouscriptionSafeCare> = [
    { title: 'Client', dataIndex: 'Numcli', key: 'Numcli' },
    { title: 'Nom', dataIndex: 'NOMCLI', key: 'NOMCLI' },
    { title: 'Prénom', dataIndex: 'PRECLI', key: 'PRECLI' },
    { title: 'Produit', dataIndex: 'NomProduit', key: 'NomProduit' },
    { title: 'Version', dataIndex: 'NumeroVersion', key: 'NumeroVersion' },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', key: 'MontantCotisation', align: 'right' },
    {
      title: 'Date effet',
      dataIndex: 'DateEffet',
      key: 'DateEffet',
      render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '',
    },
    { title: 'Statut', dataIndex: 'Statut', key: 'Statut' },
  ];

  const columnsEcheances: ColumnsType<EcheanceAssistance> = [
    { title: 'Date échéance', dataIndex: 'DateEcheance', width: 120 },
    { title: 'Montant', dataIndex: 'MontantCotisation', width: 100 },
    { title: 'Montant payé', dataIndex: 'MontantPaye', width: 100 },
    { title: 'Date paiement', dataIndex: 'DatePaiement', width: 120 },
    {
      title: 'Statut',
      dataIndex: 'Statut',
      width: 100,
      render: (s: string) => <Tag color={statutColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          disabled={record.Statut !== 'A_PAYER' && record.Statut !== 'IMPAYEE'}
          onClick={() => openPrelever(record)}
        >
          Prélever
        </Button>
      ),
    },
  ];

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span id="Tab"><MoneyCollectOutlined /> Cotisations manuelles</span>,
      children: null,
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - COTISATIONS</b>
      </Typography>
      <Spin spinning={loading || isLoading} size="large" style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign="left" style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!selectedSouscription} onClick={handleGenerer}>
                  Générer les échéances
                </Button>
                <ExportMenu disabled={!selectedSouscription || !echeances.length} onExport={(f) => exporter(f as 'pdf' | 'xlsx' | 'docx')} formats={EXPORT_FORMATS_DOCS} />
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block onClick={actualiser}>
                  Actualiser
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={annuler}>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs size="small" type="card" defaultActiveKey="1" items={items} animated={{ inkBar: true, tabPane: true }} />
            </div>
          </div>
        </Form>
        <div className="tabItem" style={{ display: 'block' }}>
          <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
            <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
              <legend>Critères de recherche</legend>
              <Space wrap>
                <Input.Search
                  placeholder="Rechercher une souscription..."
                  allowClear
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 300 }}
                />
              </Space>
            </Box>

            <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
              <legend>Souscriptions actives ({filteredData.length})</legend>
              <Table
                size="small"
                rowKey="IdSouscription"
                rowSelection={rowSelection}
                columns={columnsSous}
                dataSource={filteredData}
                scroll={{ x: 10 }}
                pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
              />
            </Box>

            {selectedSouscription ? (
              <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                <legend>
                  Tableau des échéances — {selectedSouscription.NOMCLI} {selectedSouscription.PRECLI} ({selectedSouscription.NomProduit})
                </legend>
                <Spin spinning={loadingEcheances} size="large" style={{ color: '#116599' }}>
                  <Table
                    size="small"
                    rowKey="IdEcheance"
                    columns={columnsEcheances}
                    dataSource={echeances}
                    scroll={{ x: 10 }}
                    pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
                  />
                </Spin>
              </Box>
            ) : (
              <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
                <Box sx={{
                  py: 4, px: 2,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--lightColor002)',
                    color: 'var(--darkColor)',
                    fontSize: 28,
                  }}>
                    ☝
                  </Box>
                  <Typography sx={{ fontWeight: 600, color: 'var(--darkblue)', fontSize: 15 }}>
                    Sélectionnez une souscription pour afficher son échéancier
                  </Typography>
                  <Typography sx={{ color: 'var(--colorOpaque060)', fontSize: 13 }}>
                    Choisissez un client dans le tableau ci-dessus pour consulter, générer et exporter ses échéances de cotisation.
                  </Typography>
                </Box>
              </Box>
            )}
          </div>
        </div>
      </Spin>

      <Modal
        title="Prélèvement d'échéance"
        open={modalPrelever}
        onOk={confirmPrelever}
        onCancel={() => setModalPrelever(false)}
        okText="Confirmer le prélèvement"
        cancelText="Annuler"
        okButtonProps={{ loading: loading }}
        centered
        zIndex={1400}
        className="safeCareModal"
      >
        {selectedEcheance && (
          <Box sx={{ mb: 2 }}>
            <p>Date échéance : <b>{selectedEcheance.DateEcheance}</b></p>
            <p>Montant : <b>{selectedEcheance.MontantCotisation} FCFA</b></p>
          </Box>
        )}
        <div>
          <label>Code opération (CODOPE) — défini sur le produit :</label>
          <Input
            value={codope}
            readOnly
            placeholder="Code opération du produit"
            suffix={selectedSouscription?.lib_CodOpeCotisation ?? undefined}
          />
        </div>
      </Modal>

      <ExportFileModal file={exportFile} onDone={() => setExportFile(null)} />
    </div>
  );
};

export default CotisationsPage;
